import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Globe, User } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export const Navigation = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [isLogoAnimated, setIsLogoAnimated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const timerRef = useRef<number | null>(null);
  const [initialAnimationPlayed, setInitialAnimationPlayed] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Clear any ongoing animation when location changes
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    if (isLogoAnimated) {
      setIsLogoAnimated(false);
    }
    
    if (location.pathname === '/' || !initialAnimationPlayed) {
      setIsLogoAnimated(true);
      setInitialAnimationPlayed(true);
      
      timerRef.current = window.setTimeout(() => {
        setIsLogoAnimated(false);
        timerRef.current = null;
      }, 2000);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location.pathname, initialAnimationPlayed]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (!isLogoAnimated) {
      setIsLogoAnimated(true);
    }
  };

  const handleMouseLeave = () => {
    if (!timerRef.current) {
      timerRef.current = window.setTimeout(() => {
        setIsLogoAnimated(false);
        timerRef.current = null;
      }, 1000);
    }
  };

  const toggleLanguage = () => {
    const newLanguage = language === 'am' ? 'en' : 'am';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const navLinks = [
    //...(user ? [{ to: '/saved-items', label: t('savedItems') }] : []),
  ];

  const authLinks = user
    ? [
        { to: '/profile', label: t('profile') },
        { label: t('logout'), onClick: logout }
      ]
    : [
        { to: '/login', label: t('login') },
        { to: '/signup', label: t('signup') },
      ];

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 
        ${isScrolled ? 'py-1' : 'py-2'}`}
      style={{
        background: isScrolled 
          ? 'color-mix(in srgb, var(--theme-bg-soft) 94%, transparent)'
          : 'color-mix(in srgb, var(--theme-bg-soft) 90%, transparent)',
        backdropFilter: 'blur(10px)',
        boxShadow: isScrolled
          ? 'var(--theme-shadow)'
          : '0 8px 20px rgba(0, 0, 0, 0.3)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="relative">
                <img 
                  src="/logo.svg" 
                  alt="Logo" 
                  className={`h-12 w-auto transition-all duration-1000 ease-in-out
                    ${isLogoAnimated ? 'animate-bounce' : ''}`}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
                <div 
                  className={`absolute inset-0 rounded-full bg-primary-400 opacity-0 blur-xl
                    ${isLogoAnimated ? 'animate-logo-glow' : ''}`}
                  style={{ zIndex: -1 }}
                />
              </div>
              
              <span 
                className={`font-bold text-2xl text-white transition-all duration-500
                  ${isLogoAnimated ? 'animate-text-focus-in' : ''}`}
              >
                {t('appName')}
              </span>
            </Link>
          </div>

          {/* Main Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 
                  ${location.pathname === link.to
                    ? 'text-primary-300'
                    : 'text-gray-300 hover:text-primary-300'}`}
                style={{
                  background: location.pathname === link.to 
                    ? 'linear-gradient(145deg, var(--theme-surface-2), var(--theme-bg-soft))'
                    : 'transparent',
                  boxShadow: location.pathname === link.to
                    ? 'inset 2px 2px 5px rgba(0,0,0,0.25), inset -2px -2px 5px rgba(255,255,255,0.05)'
                    : 'none',
                }}
              >
                {link.label}
                {location.pathname === link.to && (
                  <span 
                    className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-primary-500 rounded-full"
                    style={{
                      boxShadow: '0 0 10px rgba(59, 130, 246, 0.5)'
                    }}
                  ></span>
                )}
              </Link>
            ))}
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-2">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-2 rounded-full text-gray-300 hover:text-primary-300 transition-colors duration-200 font-medium"
              aria-label={language === 'en' ? 'Switch to Amharic' : 'Switch to English'}
              style={{
                background: 'linear-gradient(145deg, var(--theme-surface-2), var(--theme-surface))',
                boxShadow: '2px 2px 5px rgba(0,0,0,0.25), -2px -2px 5px rgba(255,255,255,0.03)'
              }}
            >
              {language === 'en' ? 'አማ' : 'en'}
            </button>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center space-x-2">
              {user ? (
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-gray-200"
                    style={{
                      background: isProfileOpen 
                        ? 'linear-gradient(145deg, var(--theme-bg-soft), var(--theme-surface-2))'
                        : 'linear-gradient(145deg, var(--theme-surface-2), var(--theme-surface))',
                      boxShadow: isProfileOpen
                        ? 'inset 2px 2px 5px rgba(0,0,0,0.25), inset -2px -2px 5px rgba(255,255,255,0.04)'
                        : '2px 2px 5px rgba(0,0,0,0.25), -2px -2px 5px rgba(255,255,255,0.04)'
                    }}
                  >
                    <User className="h-4 w-4" />
                    <span className="max-w-[100px] truncate">{user.name || user.email}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileOpen && (
                    <div 
                      className="absolute right-0 mt-2 w-48 rounded-xl overflow-hidden z-10"
                      style={{
                        background: 'var(--theme-surface)',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.35)',
                        animation: 'slideDown 0.2s ease-out forwards'
                      }}
                    >
                      <div className="py-1">
                        <Link
                          to="/profile"
                          className="block px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-primary-300"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          {t('profile')}
                        </Link>
                        <button
                          onClick={() => {
                            setIsProfileOpen(false);
                            logout();
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-primary-300"
                        >
                          {t('logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm font-medium text-gray-200 rounded-full transition-all duration-200"
                    style={{
                      background: 'linear-gradient(145deg, var(--theme-surface-2), var(--theme-surface))',
                      boxShadow: '2px 2px 5px rgba(0,0,0,0.2), -2px -2px 5px rgba(255,255,255,0.03)'
                    }}
                  >
                    {t('login')}
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-2 text-sm font-medium text-white rounded-full transition-all duration-200"
                    style={{
                      background: 'linear-gradient(145deg, var(--theme-primary), var(--theme-primary-strong))',
                      boxShadow: '2px 2px 5px rgba(0,0,0,0.1), -2px -2px 5px rgba(255,255,255,0.1)'
                    }}
                  >
                    {t('signup')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button - Now visible on all screens */}
            <button
              onClick={onMenuClick}
              className="p-2 rounded-full text-gray-300 hover:text-primary-300 transition-all duration-200"
              aria-label="Open Menu"
              style={{
                background: 'linear-gradient(145deg, var(--theme-surface-2), var(--theme-surface))',
                boxShadow: '2px 2px 5px rgba(0,0,0,0.2), -2px -2px 5px rgba(255,255,255,0.03)'
              }}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

    </nav>
  );
};
